import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormProvider } from '@tq/ui-components/dist/components/forms-engine';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
} from '@tq/ui-components';
import { ManageTeamInviteUserProps } from './fragments/ManageTeamInviteUserProps';
import { InviteAdminUserForm } from './InviteAdminUserForm';
import { UseInviteUserForm } from './hooks';

export const InviteUserForm: React.FC<ManageTeamInviteUserProps> = ({
  buttonText,
  pageType,
  teamOptions,
  disabled = false,
}) => {
  const { t } = useTranslation('manage_team');
  const {
    emailIsInvalid,
    nameIsInvalid,
    canSubmit,
    onUpdateEmail,
    emailOnBlur,
    onUpdateName,
    nameOnBlur,
    onSendInvite,
    inviteName,
    inviteEmail,
  } = UseInviteUserForm({ pageType });
  return (
    <>
      <FormProvider>
        <Form id='manage-user-invite-form' onValidSubmit={onSendInvite}>
          <Stack spacing={{ base: 8, lg: 0 }}>
            <Stack spacing={4} width='100%' direction={{ base: 'column', lg: 'row' }}>
              <FormControl width='100%' isInvalid={nameIsInvalid}>
                <FormLabel htmlFor='name-invite'>{t('name_label', 'Name')}:</FormLabel>
                <Input
                  id='name-invite'
                  type='text'
                  width='100%'
                  value={inviteName}
                  onChange={onUpdateName}
                  onBlur={nameOnBlur}
                  mb={0}
                  placeholder={t('user_name_placeholder', 'User name')}
                  disabled={disabled}
                />
                <FormErrorMessage role='alert' data-testid='invalid-name'>
                  {t('name_error', 'Please add a name to continue')}
                </FormErrorMessage>
              </FormControl>
              <FormControl width='100%' isInvalid={emailIsInvalid}>
                <FormLabel htmlFor='email-invite'>{t('email_label', 'Email')}:</FormLabel>
                <Input
                  id='email-invite'
                  type='email'
                  width='100%'
                  value={inviteEmail}
                  onChange={onUpdateEmail}
                  onBlur={emailOnBlur}
                  mb={0}
                  placeholder={t('email_placeholder', 'User email address')}
                  disabled={disabled}
                />
                <FormErrorMessage role='alert' data-testid='invalid-email'>
                  {t('email_error', 'Please enter a valid email address')}
                </FormErrorMessage>
              </FormControl>
              <InviteAdminUserForm
                pageType={pageType}
                teamsLabel={t('teams_label', 'Team(s)')}
                items={teamOptions}
                disabled={disabled}
                permissionLabel={t('permission_label', 'Permissions')}
              />
            </Stack>
            <Flex justifyContent={{ base: 'center', lg: 'flex-end' }}>
              <FormControl width={{ base: '100%', lg: 'unset' }}>
                <Button
                  mt={{ base: 0, lg: '2.35rem' }}
                  isFullWidth
                  type='submit'
                  maxWidth={{ base: '100%', lg: '245px' }}
                  isDisabled={!canSubmit}
                  px={8}
                  py={2}
                >
                  {buttonText}
                </Button>
              </FormControl>
            </Flex>
          </Stack>
        </Form>
      </FormProvider>
    </>
  );
};
